import { useCallback, useEffect, useMemo, useState } from "react";
import api, { getErrorMessage, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ClipboardList, AlertTriangle, FileDown } from "lucide-react";
import { useSearchParams } from "react-router-dom";

const STATUS_STYLE = {
  COMPLETED: "bg-green-100 text-green-800 border-green-300",
  LOCKED: "bg-green-100 text-green-800 border-green-300",
  READY_FOR_CONCLUSION: "bg-amber-100 text-amber-800 border-amber-300",
  READY_FOR_REVIEW: "bg-amber-100 text-amber-800 border-amber-300",
  REVIEW_NOTES_OPEN: "bg-amber-100 text-amber-800 border-amber-300",
  PENDING_EVIDENCE: "bg-amber-100 text-amber-800 border-amber-300",
  IN_PROGRESS: "bg-blue-100 text-blue-800 border-blue-300",
  NOT_STARTED: "bg-slate-100 text-slate-700 border-slate-300",
  NOT_APPLICABLE: "bg-slate-100 text-slate-700 border-slate-300",
  BLOCKED: "bg-red-100 text-red-800 border-red-300",
  STALE: "bg-red-100 text-red-800 border-red-300",
  MATERIALITY_NOT_SET: "bg-amber-100 text-amber-800 border-amber-300",
  MATERIALITY_BOUND: "bg-green-100 text-green-800 border-green-300",
  EXACT_OBJECT_LINK: "bg-green-100 text-green-800 border-green-300",
  UNMAPPED: "bg-slate-100 text-slate-700 border-slate-300",
};

const LABELS = {
  NOT_STARTED: "Belum dimulai", IN_PROGRESS: "Dikerjakan",
  PENDING_EVIDENCE: "Menunggu bukti", READY_FOR_REVIEW: "Siap direviu",
  REVIEW_NOTES_OPEN: "Catatan reviu terbuka", READY_FOR_CONCLUSION: "Siap disimpulkan",
  COMPLETED: "Selesai", NOT_APPLICABLE: "Tidak berlaku", BLOCKED: "Tertahan",
  STALE: "Usang", LOCKED: "Terkunci",
  EXACT_OBJECT_LINK: "Objek sumber persis", UNMAPPED: "Belum ada objek sumber",
  MATERIALITY_BOUND: "Terikat materialitas perikatan",
  MATERIALITY_NOT_SET: "Materialitas belum ditetapkan",
};

const DEPENDENCY_LABELS = {
  trial_balance: "Neraca saldo",
  materiality: "Materialitas",
  risk_register: "Register risiko",
  walkthrough: "Walkthrough",
  audit_areas: "Keputusan area audit",
};

// Which module owns each canonical paper, so the auditor is sent where the decision belongs.
const OWNER = {
  "A-01": "Penerimaan Perikatan", "A-02": "Independensi",
  "A-15": "Strategi Audit", "A-16": "Program Audit",
  "A-17": "Materialitas", "A-18": "Materialitas", "A-19": "Materialitas", "A-20": "Materialitas",
  "A-21": "Area Audit", "A-22": "Area Audit", "A-23": "Area Audit",
  "A-28": "Daftar Permintaan Klien", "A-30": "Pusat Kecurangan",
  "B-07": "Pusat Kecurangan", "B-08": "Pusat Kecurangan", "B-13": "Pusat Kecurangan",
  "B-09": "Pihak Berelasi",
  "B-14": "Register Risiko", "B-15": "Register Risiko", "B-16": "Register Risiko",
  "B-17": "Register Risiko", "B-18": "Respons Audit",
  "B-04": "Walkthrough", "B-05": "Walkthrough", "B-20": "Walkthrough", "B-21": "Walkthrough",
  "B-22": "Walkthrough", "B-23": "Walkthrough", "B-24": "Walkthrough", "B-25": "Walkthrough",
  "B-26": "Walkthrough",
  "B-06": "Uji Pengendalian", "B-19": "Uji Pengendalian", "B-27": "Uji Pengendalian",
  "B-28": "Uji Pengendalian", "B-29": "Uji Pengendalian", "B-30": "Uji Pengendalian",
};

const TABS = [
  ["ringkasan", "Ringkasan"],
  ["coa", "COA & FS Mapping"],
  ["materialitas", "Materialitas"],
  ["akun-signifikan", "Akun Signifikan"],
  ["cycle-scoping", "Cycle Scoping"],
  ["strategi", "Audit Strategy"],
  ["rencana", "Rencana Audit Rinci"],
  ["kesiapan", "Standards Readiness"],
  ["perencanaan", "Perencanaan (A)"],
  ["risiko", "Risiko & Pengendalian (B)"],
  ["daftar-periksa", "Daftar Periksa"],
  ["ketergantungan", "Ketergantungan & Kekinian"],
];

const AVAILABILITY_LABELS = {
  AVAILABLE: "Modul tersedia", PARTIAL: "Modul sebagian",
  INDEXED_ONLY: "Perlu KKA umum", NOT_BUILT: "Belum dibangun",
};

const COMPLETENESS_LABELS = {
  COMPLETE: "Lengkap", PARTIAL: "Sebagian", UNAVAILABLE: "Belum tersedia",
};

const text = (value) => LABELS[value] || value || "—";

function Badge({ status, label }) {
  return (
    <span className={`ml-2 font-normal inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}`}>
      {label || text(status)}
    </span>
  );
}

function Figure({ label, value, status }) {
  return (
    <div className="border border-border rounded-sm p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className={`font-medium tabular-nums ${status ? `inline-block mt-1 px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}` : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Papers({ rows, testid }) {
  return (
    <div className="space-y-2 text-xs" data-testid={testid}>
      <table className="w-full audit-table text-xs">
        <thead><tr>
          <th>Ref</th><th>Kertas kerja</th><th>Status</th><th>Objek sumber</th>
          <th>Penyusun</th><th>Pereviu</th><th>Simpulan</th><th>Dasar</th><th>Modul pemilik</th>
        </tr></thead>
        <tbody>{rows.map((row) => (
          <tr key={row.wp_ref} data-testid={`planning-row-${row.wp_ref}`}>
            <td className="font-mono">{row.wp_ref}</td>
            <td>{row.title}</td>
            <td><Badge status={row.status || "NOT_STARTED"} /></td>
            <td className="font-mono">
              {(row.source_links || []).length
                ? (row.source_links || []).map((l) => l.source_collection).join(", ")
                : <Badge status="UNMAPPED" />}
            </td>
            <td>{row.prepared_by || "—"}</td>
            <td>{row.reviewed_by || "—"}</td>
            <td>{row.audit_conclusion
              ? <span className="text-green-800">Tercatat</span>
              : <span className="text-muted-foreground">Belum ditulis</span>}</td>
            <td>{row.status_basis || row.mapping_note || "—"}</td>
            <td>{OWNER[row.wp_ref] || "—"}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Checklist({ items, rows, gate, testid }) {
  const byRef = Object.fromEntries((rows || []).map((r) => [r.wp_ref, r]));
  const done = (row) => row && ["COMPLETED", "LOCKED", "READY_FOR_CONCLUSION",
    "READY_FOR_REVIEW"].includes(row.status);
  return (
    <div className="space-y-2 text-xs" data-testid={testid}>
      {gate && (
        <div className={`border rounded-sm p-2 ${gate.status === "BLOCKED"
          ? "border-red-300 text-red-900" : "border-amber-300 text-amber-900"}`}>
          <span className="font-mono">{gate.wp_ref}</span>
          <Badge status={gate.status} />
          <div className="pt-1">{gate.status_basis || gate.mapping_note}</div>
        </div>
      )}
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Ref</th><th>Butir</th><th>Status</th></tr></thead>
        <tbody>{(items || []).map((item) => {
          const row = byRef[item.wp_ref];
          return (
            <tr key={item.wp_ref} data-testid={`checklist-${item.wp_ref}`}>
              <td className="font-mono">{item.wp_ref}</td>
              <td>{item.label}</td>
              <td>{done(row)
                ? <Badge status="COMPLETED" label="Tuntas" />
                : <Badge status={(row && row.status) || "NOT_STARTED"} />}</td>
            </tr>);
        })}
        </tbody>
      </table>
    </div>
  );
}

// Perencanaan & Risiko — bagian A dan B binder kanonik, dibaca dari modul yang memuat
// pekerjaannya. Tidak ada keputusan audit yang dibuat atau disimpulkan di sini.
export default function PlanningRisk({ id }) {
  const [view, setView] = useState(null);
  const [tab, setTab] = useState("ringkasan");
  const [board, setBoard] = useState(null);

  const load = useCallback(() => {
    api.get(`/engagements/${id}/master-binder/planning-risk`)
      .then(({ data }) => setView(data))
      .catch((e) => toast.error(getErrorMessage(e)));
    api.get(`/engagements/${id}/planning-dashboard`)
      // The dashboard is a distinct payload; accept it only when it carries its own shape.
      .then(({ data }) => setBoard(data && data.coa && data.readiness ? data : null))
      .catch(() => setBoard(null));
  }, [id]);

  const download = (path, name) =>
    downloadFile(`/engagements/${id}/${path}`, name)
      .catch((e) => toast.error(getErrorMessage(e)));
  useEffect(() => { load(); }, [load]);

  const sections = useMemo(() => {
    if (!view) return { A: [], B: [] };
    return {
      A: (view.items || []).filter((r) => r.section === "A"),
      B: (view.items || []).filter((r) => r.section === "B"),
    };
  }, [view]);

  if (!view) {
    return <div className="text-xs text-muted-foreground p-4">Memuat perencanaan & risiko…</div>;
  }
  const counted = (rows, status) => rows.filter((r) => r.status === status).length;
  const withObject = (rows) => rows.filter((r) => (r.source_links || []).length).length;
  const materiality = (view.items || []).filter((r) =>
    ["A-17", "A-18", "A-19", "A-20"].includes(r.wp_ref));

  return (
    <div className="space-y-4" data-testid="planning-risk">
      <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-head font-semibold text-sm mr-2">
          <ClipboardList size={15} />Perencanaan & Risiko
        </div>
        <div className="text-xs text-muted-foreground">{view.audit_period_display}</div>
        <span className="text-xs" data-testid="planning-materiality-status">
          Materialitas<Badge status={view.materiality_status} />
        </span>
      </div>

      {view.materiality_status === "MATERIALITY_NOT_SET" && (
        <div className="bg-white border border-amber-300 rounded-sm p-3 text-xs text-amber-900"
          data-testid="planning-materiality-absent">
          <AlertTriangle size={14} className="inline mr-1" />
          Perikatan belum memuat materialitas. Materialitas tidak diambil dari sumber lain mana
          pun — tolok ukur dan persentasenya ditetapkan auditor pada modul Materialitas.
        </div>
      )}

      <div className="bg-white border border-border rounded-sm">
        <div className="flex flex-wrap gap-1 border-b border-border p-2">
          {TABS.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              data-testid={`planning-tab-${key}`}
              className={`text-xs px-2 py-1 rounded-sm border ${tab === key
                ? "bg-slate-900 text-white border-slate-900" : "border-border"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="p-3">
          {tab === "ringkasan" && (
            board ? (
              <div className="space-y-3 text-xs" data-testid="planning-board">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Figure label="Akun terklasifikasi"
                    value={`${board.coa.coverage.classified_accounts}/${board.coa.coverage.accounts}`} />
                  <Figure label="Materialitas"
                    value={board.materiality.approved ? "Ditetapkan" : "Belum ditetapkan"}
                    status={board.materiality.approved ? "MATERIALITY_BOUND" : "MATERIALITY_NOT_SET"} />
                  <Figure label="Akun menunggu keputusan"
                    value={board.significant_accounts.requires_judgment} />
                  <Figure label="Kandidat risiko" value={board.risk_candidates.count} />
                  <Figure label="Prosedur pada pustaka" value={board.program_library.procedures} />
                  <Figure label="Strategi terisi otomatis" value={board.strategy.auto_populated} />
                  <Figure label="Strategi perlu auditor"
                    value={board.strategy.auditor_input_required} />
                  <Figure label="Kelengkapan binder" value={`${board.completeness}%`} />
                </div>

                <div className="bg-white border border-border rounded-sm p-2"
                  data-testid="planning-pending-actions">
                  <div className="font-head font-semibold pb-1">
                    Tindakan yang menunggu auditor ({board.pending_auditor_actions.length})
                  </div>
                  {board.pending_auditor_actions.length === 0 && (
                    <div className="text-muted-foreground">Tidak ada tindakan tertunda.</div>
                  )}
                  <table className="w-full audit-table text-xs">
                    <tbody>{board.pending_auditor_actions.map((action) => (
                      <tr key={action.action} data-testid={`pending-${action.action}`}>
                        <td className="font-medium">{action.action}</td>
                        <td>{action.detail}</td>
                        <td className="text-muted-foreground">{action.owner_module}</td>
                      </tr>))}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline"
                    data-testid="planning-export-strategy"
                    onClick={() => download("audit-strategy-draft.docx",
                      "Audit_Strategy_Draft.docx")}>
                    <FileDown size={13} className="mr-1" />Audit Strategy (DOCX)
                  </Button>
                  <Button size="sm" variant="outline"
                    data-testid="planning-export-plan"
                    onClick={() => download("detailed-audit-plan.xlsx",
                      "Detailed_Audit_Plan.xlsx")}>
                    <FileDown size={13} className="mr-1" />Rencana Audit Rinci (XLSX)
                  </Button>
                  <Button size="sm" variant="outline"
                    data-testid="planning-export-coa"
                    onClick={() => download("coa-mapping.xlsx", "COA_FS_Mapping.xlsx")}>
                    <FileDown size={13} className="mr-1" />COA & FS Mapping (XLSX)
                  </Button>
                </div>
                <div className="text-muted-foreground">{board.basis}</div>
              </div>
            ) : <div className="text-muted-foreground">Memuat papan perencanaan…</div>
          )}

          {tab === "coa" && <CoaMoved />}

          {tab === "akun-signifikan" && (
            board ? (
              <div className="space-y-2 text-xs" data-testid="planning-significant">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Figure label="Diusulkan signifikan"
                    value={board.significant_accounts.proposed_significant} />
                  <Figure label="Menunggu keputusan"
                    value={board.significant_accounts.requires_judgment} />
                  <Figure label="Materialitas tersedia"
                    value={board.significant_accounts.materiality_available ? "Ya" : "Tidak"} />
                </div>
                <table className="w-full audit-table text-xs">
                  <thead><tr>
                    <th>Akun</th><th>Nama</th><th className="text-right">Saldo</th>
                    <th>Rasio OM</th><th>Transaksi</th><th>Jurnal manual %</th>
                    <th>Status usulan</th><th>Mengapa</th>
                  </tr></thead>
                  <tbody>{(board.significant_accounts.accounts || []).slice(0, 200).map((a) => (
                    <tr key={a.account_code} data-testid={`significant-${a.account_code}`}>
                      <td className="font-mono">{a.account_code}</td>
                      <td>{a.account_name}</td>
                      <td className="text-right tabular-nums">{a.ending_balance ?? "—"}</td>
                      <td>{a.overall_materiality_ratio ?? "—"}</td>
                      <td className="tabular-nums">{a.transaction_count}</td>
                      <td>{a.manual_journal_ratio ?? "—"}</td>
                      <td>{a.status}</td>
                      <td>{(a.why || []).join(" ") || a.note || "—"}</td>
                    </tr>))}
                  </tbody>
                </table>
                <div className="text-muted-foreground">{board.significant_accounts.basis}</div>
              </div>
            ) : <div className="text-muted-foreground">Memuat…</div>
          )}

          {tab === "cycle-scoping" && (
            board ? (
              <div className="space-y-2 text-xs" data-testid="planning-scoping">
                <table className="w-full audit-table text-xs">
                  <thead><tr>
                    <th>Siklus</th><th>Nama</th><th>Akun</th><th>Usulan signifikan</th>
                    <th>Volume</th><th>Ketersediaan modul</th><th>Usulan lingkup</th>
                    <th>Keputusan auditor</th><th>Mengapa</th>
                  </tr></thead>
                  <tbody>{(board.cycle_scoping.cycles || []).map((c) => {
                    const avail = (board.module_availability.cycles || [])
                      .find((m) => m.cycle === c.cycle) || {};
                    return (
                      <tr key={c.cycle} data-testid={`scoping-${c.cycle}`}>
                        <td className="font-mono">{c.cycle}</td>
                        <td>{c.label}</td>
                        <td className="tabular-nums">{(c.financial_statement_accounts || []).length}</td>
                        <td className="tabular-nums">{c.proposed_significant}</td>
                        <td className="tabular-nums">{c.transaction_volume}</td>
                        <td>{AVAILABILITY_LABELS[avail.status] || avail.status || "—"}</td>
                        <td>{c.status}</td>
                        <td>{c.auditor_scope
                          ? c.auditor_scope
                          : <span className="text-muted-foreground">Belum diputuskan</span>}</td>
                        <td>{(c.why || []).join(" ")}</td>
                      </tr>);
                  })}
                  </tbody>
                </table>
                <div className="text-muted-foreground">{board.cycle_scoping.basis}</div>
              </div>
            ) : <div className="text-muted-foreground">Memuat…</div>
          )}

          {tab === "strategi" && (
            board ? (
              <div className="space-y-2 text-xs" data-testid="planning-strategy">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Figure label="Terisi otomatis" value={board.strategy.auto_populated} />
                  <Figure label="Perlu diisi auditor"
                    value={board.strategy.auditor_input_required} />
                  <Figure label="Sumber belum tersedia"
                    value={board.strategy.source_not_available} />
                  <Figure label="Disetujui auditor" value={board.strategy.auditor_approved} />
                </div>
                <div className="text-muted-foreground">{board.strategy.basis}</div>
                <Button size="sm" variant="outline" data-testid="strategy-export"
                  onClick={() => download("audit-strategy-draft.docx",
                    "Audit_Strategy_Draft.docx")}>
                  <FileDown size={13} className="mr-1" />Unduh Draf Strategi (DOCX)
                </Button>
              </div>
            ) : <div className="text-muted-foreground">Memuat…</div>
          )}

          {tab === "rencana" && (
            board ? (
              <div className="space-y-2 text-xs" data-testid="planning-plan">
                {board.program_library.empty && (
                  <div className="bg-white border border-amber-300 rounded-sm p-2 text-amber-900"
                    data-testid="plan-library-gap">
                    <AlertTriangle size={13} className="inline mr-1" />
                    Pustaka program audit belum tersedia. Tidak ada prosedur yang dapat diusulkan
                    sebagai respons atas risiko, dan sistem tidak membuat prosedur hanya untuk
                    menutup kesenjangan ini.
                  </div>
                )}
                <Button size="sm" variant="outline" data-testid="plan-export"
                  onClick={() => download("detailed-audit-plan.xlsx",
                    "Detailed_Audit_Plan.xlsx")}>
                  <FileDown size={13} className="mr-1" />Unduh Rencana Audit Rinci (XLSX)
                </Button>
              </div>
            ) : <div className="text-muted-foreground">Memuat…</div>
          )}

          {tab === "kesiapan" && (
            board ? (
              <div className="space-y-2 text-xs" data-testid="planning-readiness">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(board.readiness.by_priority).map(([level, count]) => (
                    <Figure key={level} label={level} value={count} />
                  ))}
                </div>
                <table className="w-full audit-table text-xs">
                  <thead><tr><th>Prioritas</th><th>Kesenjangan</th><th>Catatan</th></tr></thead>
                  <tbody>{(board.readiness.planning_gaps || []).map((g) => (
                    <tr key={g.gap} data-testid={`gap-${g.gap}`}>
                      <td>{g.priority}</td><td className="font-mono">{g.gap}</td>
                      <td>{g.note}</td>
                    </tr>))}
                  </tbody>
                </table>
              </div>
            ) : <div className="text-muted-foreground">Memuat…</div>
          )}

          {tab === "perencanaan" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
                data-testid="planning-summary-a">
                <Figure label="Kertas kerja perencanaan" value={sections.A.length} />
                <Figure label="Dengan objek sumber" value={withObject(sections.A)} />
                <Figure label="Belum dimulai" value={counted(sections.A, "NOT_STARTED")} />
                <Figure label="Tertahan" value={counted(sections.A, "BLOCKED")} />
              </div>
              <Papers rows={sections.A} testid="planning-papers-a" />
              <div className="text-xs text-muted-foreground">{view.basis}</div>
            </div>
          )}

          {tab === "risiko" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
                data-testid="planning-summary-b">
                <Figure label="Kertas kerja risiko" value={sections.B.length} />
                <Figure label="Dengan objek sumber" value={withObject(sections.B)} />
                <Figure label="Belum dimulai" value={counted(sections.B, "NOT_STARTED")} />
                <Figure label="Tertahan" value={counted(sections.B, "BLOCKED")} />
              </div>
              <Papers rows={sections.B} testid="planning-papers-b" />
            </div>
          )}

          {tab === "materialitas" && (
            <div className="space-y-2 text-xs" data-testid="planning-materiality">
              {board && (
                <table className="w-full audit-table text-xs" data-testid="materiality-benchmarks">
                  <thead><tr>
                    <th>Tolok ukur</th><th className="text-right">Jumlah</th>
                    <th className="text-right">Periode lalu</th><th>Cakupan %</th>
                    <th>Kelengkapan</th><th>Kandidat OM</th><th>Catatan</th>
                  </tr></thead>
                  <tbody>{board.benchmarks.map((b) => (
                    <tr key={b.benchmark} data-testid={`benchmark-${b.benchmark}`}>
                      <td>{b.label}</td>
                      <td className="text-right tabular-nums">{b.amount ?? "—"}</td>
                      <td className="text-right tabular-nums">{b.prior_amount ?? "—"}</td>
                      <td>{b.classification_coverage_pct
                        ? String(b.classification_coverage_pct).slice(0, 5) : "—"}</td>
                      <td>{COMPLETENESS_LABELS[b.completeness] || b.completeness}</td>
                      <td className="text-right tabular-nums">
                        {b.candidate_overall_materiality ?? "—"}</td>
                      <td>{b.reason || b.note}</td>
                    </tr>))}
                  </tbody>
                </table>
              )}
              <Papers rows={materiality} testid="planning-papers-materiality" />
              <div className="text-muted-foreground">
                Tolok ukur, persentase dan alasannya ditetapkan auditor pada modul Materialitas.
                Binder tidak memilih tolok ukur, tidak memilih persentase, dan tidak mengambil
                materialitas dari keputusan ukuran sampel mana pun. Perubahan materialitas
                menandai rencana sampling yang terikat padanya sebagai usang.
              </div>
            </div>
          )}

          {tab === "daftar-periksa" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-head font-semibold pb-2">
                  Penyelesaian Perencanaan (A-31)
                </div>
                <Checklist items={view.planning_checklist} rows={view.items}
                  gate={(view.checklists || {})["A-31"]} testid="planning-checklist-a" />
              </div>
              <div>
                <div className="text-xs font-head font-semibold pb-2">
                  Simpulan Penilaian Risiko (B-32)
                </div>
                <Checklist items={view.risk_checklist} rows={view.items}
                  gate={(view.checklists || {})["B-32"]} testid="planning-checklist-b" />
              </div>
            </div>
          )}

          {tab === "ketergantungan" && (
            <div className="space-y-2 text-xs" data-testid="planning-dependencies">
              <table className="w-full audit-table text-xs">
                <thead><tr>
                  <th>Sumber</th><th>Kertas kerja yang bergantung</th><th>Akibat perubahan</th>
                </tr></thead>
                <tbody>{Object.entries(view.dependencies || {}).map(([key, entry]) => (
                  <tr key={key} data-testid={`dependency-${key}`}>
                    <td>{DEPENDENCY_LABELS[key] || key}</td>
                    <td className="font-mono">{(entry.dependents || []).join(", ")}</td>
                    <td>{entry.note}</td>
                  </tr>))}
                </tbody>
              </table>
              <div className="text-muted-foreground">
                Perubahan sumber menandai kertas kerja di bawahnya sebagai usang, bukan
                memperbaruinya. Auditor memutuskan apakah pekerjaan yang terpengaruh masih berlaku.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// The COA review and FS mapping are edited in one place, the TB / Mapping workspace; this planning view links
// there instead of carrying a second editor for the same accounts.
function CoaMoved() {
  const [, setSp] = useSearchParams();
  return (
    <div className="text-xs space-y-2" data-testid="planning-coa-moved">
      <p>Reviu COA & pemetaan pos LK kini berada di satu tempat: <strong>TB / Mapping › Reviu COA & Pemetaan LK</strong>. Data dan riwayatnya tidak berubah.</p>
      <button type="button" className="underline text-sky-700" data-testid="planning-open-coa-review" onClick={() => setSp({ tab: "mapping", view: "review" })}>Buka Reviu COA & Pemetaan LK</button>
    </div>
  );
}
